import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  Grid,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme,
} from '@quillbot/design-system';
import createStyles from '@mui/styles/createStyles';
import { makeStyles } from '@mui/styles';
import mammoth from 'mammoth';
import PDFJS from 'pdfjs-dist/es5/build/pdf';
import PDFJSWorker from 'pdfjs-dist/es5/build/pdf.worker.js';
import logger from 'core/logger/logger';
import { fileUploadIcon, fileUploadOnboardingIcon } from 'public/images/svgPath.json';
import { setExportedImages, toggleAlertDialogAction } from 'src/_common/actions/commonActions';
import commonTrack from 'src/_common/handlers/track';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/_common/models/redux/store';
import { useRouter } from 'next/router';
import { setUploadFileInfo } from 'src/paraphraser/actions/paraModelActions';
import { userUploadedFile } from 'src/summarizer/actions/controlActions';
import AlertDialog from '../systemNotifications/AlertDialog';
import MyInputBox from '../myInputBox/MyInputBox';

PDFJS.disableTextLayer = true;
PDFJS.disableWorker = true; // not availaible anymore since 2.3.0 (see imports)

interface IProps {
  getText: Function;
  setFileInfo: Function;
  style?: {};
  className?: string;
  droppedFile: { files: File[] | null };
  richTextRequired?: boolean;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    uploadBox: {
      // width: 'max-content',
      boxSizing: 'border-box',
      // paddingBottom: '5px',
      border: '1px solid transparent',

      // '& div': {
      //   '& span': {

      //   },
      //   '& svg': {
      //     '& path': {

      //     },
      //   },
      // },

      '&:hover': {
        '& div': {
          '& span': {
            color: theme.palette.primary.dark,
          },
          '& $uploadIcon': {
            color: theme.palette.primary.dark,
          },
        },
        // background: '#EBF2ED',
        // border: '1px solid rgba(76, 148, 90, 0.44)',
        // borderRadius: '8px',
      },
    },
    uploadIcon: {
      fontSize: '27px',
      padding: '2px',
      color: theme.specialColors.ICON_DEFAULT_COLOR,
    },
    uploadDocText: {
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
      [theme.breakpoints.down(1000)]: {
        display: 'none',
      },
    },
    tooltip: {
      maxWidth: '120px',
      textAlign: 'center',
    },
  })
);

export default function FileUploader({
  getText,
  style,
  className,
  droppedFile,
  setFileInfo,
  richTextRequired,
}: IProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = useSelector((state: RootState) => state?.common?.platform || {});
  const classes = useStyles();
  const [fileResetKey, setFileResetKey] = useState('');
  const [passwd, setPasswd] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [incorrectPassCheck, setIncorrectPassCheck] = useState(false);
  const [arrBuffer, setArrBuffer] = useState<ArrayBuffer | PDFSource>(null);
  const router = useRouter();
  // For PDF
  const getPageText = async (pdf: Pdf, pageNo: number) => {
    const page = await pdf.getPage(pageNo);
    page
      .getOperatorList()
      .then((opList: OperatorList) => {
        const svgGfx = new PDFJS.SVGGraphics(page.commonObjs, page.objs);
        svgGfx.embedFonts = true;
        // console.log(opList);
        const images: string[] = [];
        opList.fnArray.forEach((ele, i) => {
          if (ele === PDFJS.OPS.paintJpegXObject || ele === PDFJS.OPS.paintImageXObject) {
            const img = page.objs.get(opList.argsArray[i][0]);
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            // ParaModel.exportedFileImages.push(dataURL);
            images.push(dataURL);
            // const reader = new FileReader();
            // reader.readAsDataURL(imageInfo);
            // reader.onloadend = () => {
            //   const base64data = reader.result;
            //   extractedImages.push(base64data);
            // };
            // extractedImages.push(imageInfo);
          }
        });
        dispatch(setExportedImages(images));
      })
      .catch((e) => {
        logger('Erro in extracting images', e);
      });

    const tokenizedText = await page.getTextContent({
      normalizeWhitespace: false,
      disableCombineTextItems: false,
    });
    // console.log(tokenizedText.items);

    let lastY = tokenizedText.items[0].transform[5];
    let minSameParaLineDifference = Number.MAX_VALUE;

    tokenizedText.items.forEach((token) => {
      if (lastY !== token.transform[5] && minSameParaLineDifference > lastY - token.transform[5]) {
        minSameParaLineDifference = lastY - token.transform[5];
      }
    });
    let pageText = '';
    tokenizedText.items.forEach((token) => {
      if (lastY !== token.transform[5]) {
        // minSameParaLineDifference = lastY - token.transform[5];
        // console.log(
        //   token.str,
        //   minSameParaLineDifference,
        //   tokenizedText.items[i + 1] && lastY - tokenizedText.items[i + 1].transform[5]
        // );
        if (lastY - token.transform[5] > minSameParaLineDifference) {
          pageText += '\n';
        } else {
          pageText += ' ';
        }
        lastY = token.transform[5];
        // if (
        //   tokenizedText.items[i + 1] &&
        //   minSameParaLineDifference === lastY - tokenizedText.items[i + 1].transform[5]
        // ) {
        //   pageText += ' ';
        // } else {
        //   // console.log(token.str);

        //   pageText += '\n';
        // }
      }
      pageText += `${token.str}`;
    });
    return pageText;
  };

  const getPDFText = async (source: PDFSource): Promise<string> => {
    Object.assign(window, { pdfjsWorker: PDFJSWorker }); // added to fit 2.3.0
    const pdf: Pdf = await PDFJS.getDocument({
      data: source,
      nativeImageDecoderSupport: PDFJS.NativeImageDecoding.DISPLAY,
      password: passwd,
    }).promise;
    const maxPages = pdf.numPages;
    const pageTextPromises = [];
    for (let pageNo = 1; pageNo <= maxPages; pageNo += 1) {
      pageTextPromises.push(getPageText(pdf, pageNo));
    }
    const pageTexts = await Promise.all(pageTextPromises);
    return pageTexts.join(' ');
  };

  // For docx
  const parseDocxFile = (arrayBuffer: ArrayBuffer | string | null) => {
    try {
      mammoth.convertToHtml({ arrayBuffer }).then((result: { value: string }) => {
        const html = result.value; // The generated HTML
        const docxInHTML = document.createElement('div');
        docxInHTML.innerHTML = html;
        const imageCreatedPromiseArray: Promise<string>[] = [];
        Array.from(docxInHTML.getElementsByTagName('img')).forEach((img) => {
          const promise = new Promise<string>((resolve) => {
            img.onload = () => {
              resolve(img.src);
            };
          });
          // img.onload = () => {
          //   ParaModel.exportedFileImages.push(img.src);
          //   // images.push(img.src);
          // };
          imageCreatedPromiseArray.push(promise);
        });

        Promise.all(imageCreatedPromiseArray).then((images) => {
          dispatch(setExportedImages(images));
        });
        if (richTextRequired) return getText(html);
      });
      // .catch((e: unknown) => {
      //   dispatch(
      //     toggleAlertDialogAction(
      //       false,
      //       'The file failed to upload as it could be corrupted. Please choose another and try again.',
      //       'Upload Failed',
      //       true,
      //       undefined,
      //       true,
      //       'uploadFile'
      //     )
      //   );
      //   logger('Docx File parsing failed', e);
      // });
    } catch (e) {
      dispatch(
        toggleAlertDialogAction(
          false,
          'The file failed to upload as it could be corrupted. Please choose another and try again.',
          'Upload Failed',
          true,
          undefined,
          true,
          'uploadFile'
        )
      );
      logger('Docx File parsing failed', e);
    }
    try {
      mammoth.extractRawText({ arrayBuffer }).then((resultObject: { value: string }) => {
        getText(resultObject.value.trim().length > 0 ? resultObject.value : 'No data found!');
      });
      // .catch((e: unknown) => {
      //   dispatch(
      //     toggleAlertDialogAction(
      //       false,
      //       'The file failed to upload as it could be corrupted. Please choose another and try again.',
      //       'Upload Failed',
      //       true,
      //       undefined,
      //       true,
      //       'uploadFile'
      //     )
      //   );
      //   logger('Docx File parsing failed', e);
      // });
    } catch (e) {
      dispatch(
        toggleAlertDialogAction(
          false,
          'The file failed to upload as it could be corrupted. Please choose another and try again.',
          'Upload Failed',
          true,
          undefined,
          true,
          'uploadFile'
        )
      );
      logger('Docx File parsing failed', e);
    }
  };
  const fetchPDFText = (pdfArrBuffer: ArrayBuffer | PDFSource) => {
    getPDFText(pdfArrBuffer)
      .then((data) => {
        getText(data.trim().length > 0 ? data : 'No data found!');
        setDialogOpen(false);
      })
      .catch((e) => {
        if (e.message === 'No password given') {
          setDialogOpen(true);
        } else if (e.message === 'Incorrect Password') {
          setIncorrectPassCheck(true);
          setDialogOpen(true);
        } else {
          dispatch(
            toggleAlertDialogAction(
              false,
              'The file failed to upload as it could be corrupted. Please choose another and try again.',
              'Upload Failed',
              true,
              undefined,
              true,
              'uploadFile'
            )
          );
          logger('PDF file parsing failed', e);
        }
      });
  };

  const readFile = (input: { files: File[] | null | FileList }) => {
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    const fileType = file.type;
    const fileName = file.name.length > 30 ? `${file.name.substring(0, 30)}...` : file.name;
    // greater than 25MB
    if (file.size / 1048576 >= 25) {
      commonTrack.uploadedFile(fileType, `${file.size / 1048576} MB`, false);
      setFileResetKey(Math.random().toString(36));
      dispatch(
        toggleAlertDialogAction(
          false,
          'The file failed to upload as it’s larger than 25MB. Please upload a smaller document.',
          'Upload Failed',
          true,
          undefined,
          true,
          'uploadFile'
        )
      );
      return;
    }
    commonTrack.uploadedFile(fileType, `${file.size / 1024} KB`, true);
    // if (
    //   fileType === 'application/pdf' ||
    //   fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    // ) {
    //   getText('Please wait, Processing File...');
    // }

    setFileInfo(fileName, fileType);
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = () => {
      const arrayBuffer: ArrayBuffer | PDFSource = reader.result;
      setArrBuffer(arrayBuffer);
      setFileResetKey(Math.random().toString(36));
      if (fileType === 'application/pdf') {
        // getText('Please wait, Processing File...');
        fetchPDFText(arrayBuffer);
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        // getText('Please wait, Processing File...');
        parseDocxFile(arrayBuffer);
      }
    };
  };

  useEffect(() => {
    if (droppedFile.files) {
      readFile(droppedFile);
    }
  }, [droppedFile]);

  const handleSubmit = () => {
    fetchPDFText(arrBuffer);
  };
  const handleCancel = () => {
    setPasswd('');
    setIncorrectPassCheck(false);
    if (router.pathname === '/') {
      dispatch(setUploadFileInfo({ fileName: '', fileType: '' }));
    } else if (router.pathname === '/summarize') {
      dispatch(userUploadedFile({ fileName: '', fileType: '' }));
    }
    setDialogOpen(false);
  };
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncorrectPassCheck(false);
    const value = event?.target?.value;
    setPasswd(value);
  };

  return (
    <>
      <AlertDialog />
      <input
        id="fileUpload"
        style={{ ...style, display: 'none' }}
        type="file"
        className={className}
        onChange={(e) => readFile({ files: e.target.files })}
        onClick={commonTrack.clickedFileUploadButton}
        ref={fileInputRef}
        accept="application/pdf, .docx"
        key={fileResetKey}
      />
      <Dialog
        open={dialogOpen}
        style={{ display: 'relative' }}
        // onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{
            maxWidth: '320px',
            padding: '15px 15px 7px 15px',
            borderRadius: '5px',
            boxShadow: '0px 1px 6px rgb(0 0 0 / 25%)',
          }}
        >
          <MyInputBox
            error={incorrectPassCheck}
            passBorderColor="#828282"
            variant="filled"
            data-gramm_editor="false"
            wt-ignore-input="true"
            fullWidth
            id="passwd"
            name="passwd"
            type="password"
            onChange={onChange}
            size="small"
            label="Enter Password"
            value={passwd}
          />
          {incorrectPassCheck ? (
            <Grid style={{ marginInlineEnd: 'auto' }}>
              <Typography
                style={{
                  float: 'left',
                  paddingTop: '2px',
                  marginBottom: '-18px',
                  color: theme.palette.error.main,
                  fontSize: '12px',
                  height: '16px',
                }}
              >
                Incorrect Password
              </Typography>
            </Grid>
          ) : null}
          <DialogActions style={{ paddingTop: '24px', paddingRight: '0px', width: '100%' }}>
            <Grid item xs={12} style={{ float: 'right' }}>
              <Button
                style={{
                  float: 'right',
                  width: '74px',
                  height: '30px',
                  borderRadius: '3px',
                }}
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Enter
              </Button>
              <Button
                style={{
                  float: 'right',
                  width: '74px',
                  height: '30px',
                  backgroundColor: '#E0E0E0',
                  border: '0.5px solid #C4C4C4',
                  marginRight: '10px',
                  borderRadius: '3px',
                  color: '#5F6368',
                }}
                variant="contained"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Grid>
          </DialogActions>
        </Grid>
      </Dialog>
      <Tooltip
        title="Browse for a document or drag & drop one"
        placement="top"
        classes={{
          tooltip: classes.tooltip,
        }}
        arrow
      >
        <Grid style={{ width: mode.isDesktop ? 'max-content' : 'auto' }}>
          {mode.isDesktop && (
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="flex-start"
              onClick={() => fileInputRef && fileInputRef.current && fileInputRef.current.click()}
              style={{
                cursor: 'pointer',
                outline: 'none',
                paddingLeft: '15px',
                width: 'max-content',
              }}
              className={classes.uploadBox}
            >
              <div style={{ display: 'flex', alignItems: ' center', marginRight: '7.5px' }}>
                <CloudUploadOutlinedIcon className={classes.uploadIcon} />
                <Typography component="span" className={classes.uploadDocText}>
                  &nbsp; Upload Doc
                </Typography>
              </div>
            </Grid>
          )}

          {mode.isMobile && (
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="flex-start"
              onClick={() => fileInputRef && fileInputRef.current && fileInputRef.current.click()}
              style={{
                cursor: 'pointer',
                outline: 'none',
                alignItems: 'center',
              }}
              className={classes.uploadBox}
            >
              <>
                <div
                  style={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    padding: '10px',
                    width: '130px',
                    height: '74px',
                    border: `1px solid ${theme.palette.primary.dark}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    alignItems: 'center',
                    color: theme.palette.primary.main,
                  }}
                >
                  <Grid>
                    <SvgIcon width="30" height="30" viewBox="0 0 30 20">
                      <path {...fileUploadOnboardingIcon} />
                    </SvgIcon>
                  </Grid>
                  <span>Upload Doc</span>
                </div>
              </>
            </Grid>
          )}
        </Grid>
      </Tooltip>
    </>
  );
}
